import AxiosConfig from "../AxiosConfig";
import i18n from "../i18n";
import Validator from "validatorjs";

/**
 *
 * @param to
 * @param from
 * @param savedPosition
 * @returns {{x: number, y: number}|*}
 */
export function scrollBehavior (to, from, savedPosition) {
  const isModal = to.matched[1].components.modal;
  if (savedPosition || isModal) {
    return savedPosition;
  } else {
    return {
      x: 0,
      y: 0
    };
  }
}

/**
 *
 * @param errorObj
 * @param key
 * @returns {*}
 */
export function getValidationErrors (errorObj, key) {
  if (errorObj && errorObj[key]) {
    if (Array.isArray(errorObj[key])) {
      return errorObj[key][0];
    }
    return errorObj[key];
  }
  return null;
}

/**
 *
 * @param FallbackComponent
 * @returns {beforeEnter}
 */
export function modalBeforeEnterFactory(FallbackComponent) {
  return function beforeEnter(to, from, next) {
    if (FallbackComponent) {
      to.matched[1].components.default = from.matched[1] ? from.matched[1].components.default : FallbackComponent;
    }
    next();
  }
}

/**
 *
 * @param obj
 * @param value
 * @returns {string}
 */
export function getKeyByValue( obj, value ) {
  for( let prop in obj ) {
    if( obj.hasOwnProperty( prop ) ) {
      if( obj[ prop ] === value )
        return prop;
    }
  }
}

/**
 *
 * @param rows
 * @param searchString
 * @returns {function(*=): boolean}
 */
export function filterFn (searchString) {
  return function search(row) {
    // name the function so we can use recursion (thus we can't use an arrow function)
    return Object.keys(row).some(key => {
      // ...
      if (typeof row[key] === "string") {
        // if the current property is a string
        // then check if it contains the search string
        return row[key].toLowerCase().indexOf(searchString.toLowerCase()) > -1;
      } else if (row[key] && typeof row[key] === "object") {
        // otherwise, if it's an object
        return search(row[key]); // do a recursive check
      }
      return false; // return false for any other type (not really necessary as undefined will be returned implicitly)
    });
  }
}

/**
 *
 * @param count
 * @param noun
 * @param suffix
 * @returns {string}
 */
export function maybePluralize (count, noun, suffix = 's') {
  return `${count} ${noun}${count !== 1 ? suffix : ''}`;
}

/**
 *
 * @param comment
 * @returns {{all_replies: (*[]|(*|{all_replies})[]|Array)}}
 */
export function sanitizeComment (comment) {
  return {
    ...comment,
    all_replies: comment.all_replies || comment.replies
  };
}

/**
 *
 * @param item
 * @param data
 * @returns {{all_replies: {all_replies}[]}|{all_replies: *[]}}
 */
export function updateReply (item, data) {
  if (item.id === data.reply_id) {
    return {
      ...item,
      all_replies: [...item.all_replies, data]
    };
  }

  return {
    ...item,
    all_replies: item.all_replies.map(i => updateReply(i, data))
  };
}

/**
 *
 * @param item
 * @param data
 * @returns {{all_replies: (*|{all_replies})[]}}
 */
export function updateComment (item, data) {
  if (item.id === data.id) {
    return {
      ...item,
      ...data
    };
  }

  const all_replies = item.all_replies.map(i => updateComment(i, data));

  return {
    ...item,
    all_replies
  };
}

/**
 *
 * @param func
 * @param delay
 * @returns {Function}
 */
export function debounce (func, delay) {
  let inDebounce;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(inDebounce);
    inDebounce = setTimeout(() => func.apply(context, args), delay);
  }
}

/**
 *
 * @param avatarSrc
 * @returns {string}
 */
export function getPhotoSrc (avatarSrc) {
  return avatarSrc
    ? AxiosConfig.getStorageAddress() + avatarSrc.replace("storage", "")
    : "";
}

/**
 *
 * @param varName
 * @param fallback
 * @returns {string|*}
 */
export function cssVar (varName, fallback) {
  try {
    const el = document.querySelector("body");
    return window.getComputedStyle(el).getPropertyValue(varName) || fallback;
  } catch (e) {
    return fallback;
  }
}

/**
 *
 * @param resource
 * @returns {string}
 */
export function getResourceLink(resource) {
  const axiosConfig = AxiosConfig.getConfig();
  const locale = axiosConfig.headers['Accept-Language'] || 'en';
  return AxiosConfig.getStorageAddress() + "/resources/" + locale + "/" + resource.module_id + "/" + resource.filename;
}

/**
 *
 * @param lang
 */
export function setAppLang(lang) {
  let pureLang = lang;
  if(lang.includes('-')) {
    pureLang = lang.split('-')[0];
  }
  i18n.locale = pureLang;
  document.documentElement.lang = pureLang;
  document.title = i18n.t('app_title');
  AxiosConfig.setLanguage(pureLang);
  Validator.useLang(pureLang);
}

/**
 *
 * @param el
 * @returns {boolean}
 */
export function isInViewport(el) {
  let top = el.offsetTop;
  let left = el.offsetLeft;
  let width = el.offsetWidth;
  let height = el.offsetHeight;

  while(el.offsetParent) {
    el = el.offsetParent;
    top += el.offsetTop;
    left += el.offsetLeft;
  }

  return (
    top < (window.pageYOffset + window.innerHeight) &&
    left < (window.pageXOffset + window.innerWidth) &&
    (top + height) > window.pageYOffset &&
    (left + width) > window.pageXOffset
  );
}

/**
 *
 * @param el
 * @param percentVisible
 * @returns {boolean}
 */
export function isElementXPercentInViewport (el, percentVisible) {
  let
    rect = el.getBoundingClientRect(),
    windowHeight = (window.innerHeight || document.documentElement.clientHeight);

  return !(
    Math.floor(100 - (((rect.top >= 0 ? 0 : rect.top) / +-(rect.height / 1)) * 100)) < percentVisible ||
    Math.floor(100 - ((rect.bottom - windowHeight) / rect.height) * 100) < percentVisible
  )
}

/**
 *
 * @param array
 * @param searchKey
 * @param searchValue
 * @returns {number}
 */
export function findLastIndex(array, searchKey, searchValue) {
  const index = array.slice().reverse().findIndex(x => x[searchKey] === searchValue);
  const count = array.length - 1;
  return index >= 0 ? count - index : index;
}
